import { ChevronDown } from "lucide-react";
import {
  Popover,
  PopoverContent,
  PopoverTrigger,
} from "@/components/ui/popover";
import { cn } from "@/lib/utils";
import type { Temperature } from "../types";

const TEMP_OPTIONS: { value: Temperature; label: string; cls: string }[] = [
  { value: "chaud", label: "Chaud", cls: "bg-red-500" },
  { value: "a_chauffer", label: "À chauffer", cls: "bg-orange-500" },
  { value: "froid", label: "Froid", cls: "bg-blue-500" },
  { value: "perdu", label: "Perdu", cls: "bg-muted-foreground" },
];

const PILL_CLS: Record<Temperature, string> = {
  chaud: "bg-red-50 text-red-800 dark:bg-red-950/40 dark:text-red-300",
  a_chauffer:
    "bg-orange-50 text-orange-800 dark:bg-orange-950/40 dark:text-orange-300",
  froid: "bg-blue-50 text-blue-800 dark:bg-blue-950/40 dark:text-blue-300",
  perdu: "bg-muted text-muted-foreground",
};

type Props = {
  value: Temperature | null | undefined;
  onChange: (next: Temperature) => void;
};

/**
 * Pill cliquable qui ouvre un popover des 4 valeurs. Cf design/atomic-crm/header.jsx
 * (sous-composant temperature dropdown).
 */
export function TemperaturePopover({ value, onChange }: Props) {
  const current = TEMP_OPTIONS.find((o) => o.value === value);
  const label = current?.label ?? "Non défini";
  const pillCls = value ? PILL_CLS[value] : "bg-muted text-muted-foreground";
  const dotCls = current?.cls ?? "bg-muted-foreground/50";

  return (
    <Popover>
      <PopoverTrigger asChild>
        <button
          type="button"
          className={cn(
            "inline-flex items-center gap-1.5 px-2.5 py-0.5 rounded-full text-[11.5px] font-medium transition-colors hover:opacity-80",
            pillCls,
          )}
          aria-label="Modifier la température"
        >
          <span className={cn("size-2 rounded-full", dotCls)} />
          {label}
          <ChevronDown className="size-3" />
        </button>
      </PopoverTrigger>
      <PopoverContent align="start" className="w-44 p-1">
        {TEMP_OPTIONS.map((opt) => (
          <button
            key={opt.value}
            type="button"
            onClick={() => onChange(opt.value)}
            className={cn(
              "w-full flex items-center gap-2 px-2 py-1.5 text-[13px] rounded-md hover:bg-muted/60 transition-colors",
              opt.value === value && "bg-muted/40",
            )}
          >
            <span className={cn("size-2 rounded-full", opt.cls)} />
            {opt.label}
          </button>
        ))}
      </PopoverContent>
    </Popover>
  );
}
